//
// browserInfo
//
// Detect mobile browsers
// https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent
//
// Other navigator properties
//  vibrate, mediaDevices, keyboard, geolocation, credentials, cookieEnabled, connection
//  and userAgentData (Chrome): {
//    brands,
//    mobile,
//    platform,
//  }
//
import browserTouch from './browserTouch';
import config from './config';
import displayJson from './displayJson';
import logDev from './logDev';
import parseUserAgent from './parseUserAgent';

const browserInfo = () => {
  const {
    userAgent,
    language,
    onLine,
    vendor,
    cookieEnabled,
    permissions,
  } = navigator;

  const parsedUa = parseUserAgent(userAgent);

  const { osName, browserVersion } = parsedUa;
  // Brave sends Chrome's user agent and only reveals itself through this navigator property
  const browserName = (typeof navigator.brave?.isBrave === 'function') ? 'Brave' : parsedUa.browserName;

  // A user agent carrying no device hint may still carry one in Chromium's client hints
  const uaDeviceType = parsedUa.deviceType ?? ((navigator.userAgentData?.mobile) ? 'mobile' : undefined);

  // An iPad on iPadOS 13+ claims to be a Macintosh; only a real Mac reports no touch points
  const iPadOnDesktopUa = (osName === 'macOS') && ((navigator.maxTouchPoints ?? 0) > 1);
  const deviceType = (iPadOnDesktopUa) ? 'tablet' : (uaDeviceType || 'desktop');

  // Touch is the primary input, where `touch` above only says a touchscreen exists
  const touchPrimary = window.matchMedia?.('(hover: none) and (pointer: coarse)').matches ?? false;

  const mobile = (deviceType === 'mobile');

  let name = 'unknown - no userAgent';
  let isIOS = false;
  let isSafari = false;
  let isLegacyIOS = false;

  if (userAgent) {
    // Do not change the order!
    if (userAgent.includes('Chromium')) {
      name = 'Chromium';
    } else if (userAgent.includes('Chrome')) {
      name = 'Chrome';
    } else if (userAgent.includes('Seamonkey')) {
      name = 'Seamonkey';
    } else if (userAgent.includes('Firefox')) {
      name = 'Firefox';
    } else if (userAgent.includes('Safari')) {
      isSafari = true;
      name = 'Safari';
    } else if (userAgent.includes('Opera')) {
      name = 'Opera';
    } else if (userAgent.includes('OPR')) {
      name = 'Opera 15+';
    } else if (userAgent.includes('MSIE')) {
      name = 'IE 10';
    } else if (userAgent.includes('Trident/7.0')) {
      name = 'IE 11';
    } else { name = `unknown (${userAgent})`; }

    // Check if the device is running iOS
    isIOS = /iP(hone|od|ad)/.test(userAgent);
    // // Check if the browser is Safari
    // isSafari = /Safari/.test(userAgent) && !/CriOS/.test(userAgent) && !/FxiOS/.test(userAgent);

    if (isIOS && isSafari) {
      // Extract the OS version number
      const osVersionMatch = /OS (\d+)_?(\d+)?_?(\d+)? like Mac OS X/.exec(userAgent); // NOSONAR client-side, bounded/trusted input
      if (osVersionMatch) {
        const majorVersion = Number.parseInt(osVersionMatch[1], 10);
        const minorVersion = Number.parseInt(osVersionMatch[2] || '0', 10);

        // Check if the OS version is less than 16.4
        if (majorVersion < 16 || (majorVersion === 16 && minorVersion < 4)) {
          isLegacyIOS = true;
        }
      }
    }
  }

  const touch = browserTouch();

  if (config.dev) {
    logDev(`userAgent=${userAgent}`);

    logDev(`name=${name}`);
    logDev(`mobile=${mobile}`);
    logDev(`touch=${touch}`);
    logDev(`touchPrimary=${touchPrimary}`);
    logDev(`language=${language}`);
    // logDev(`vendor=${vendor}`);
    logDev(`onLine=${onLine}`);
    logDev(`permissions=${displayJson(permissions)}`);
    logDev(`isIOS=${isIOS}`);
    logDev(`isSafari=${isSafari}`);
    logDev(`isLegacyIOS=${isLegacyIOS}`);

    logDev(`deviceType=${deviceType}`);
    logDev(`osName=${osName}`);
    logDev(`browserName=${browserName}`);
    logDev(`browserVersion=${browserVersion}`);
  }

  return {
    // basic info:
    cookieEn: cookieEnabled,
    UA: userAgent,
    // Other:
    name,
    mobile,
    touch,
    touchPrimary,
    language,
    vendor,
    onLine,
    permissions,
    isIOS,
    isSafari,
    isLegacyIOS,

    deviceType,
    osName,
    browserName,
    browserVersion,
  };
};
export default browserInfo;
